import React, { useState, useEffect, useRef } from "react"
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Alert,
  Badge,
  Button,
  Input,
  Label,
  FormGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
  Progress,
  Table,
  Collapse,
} from "reactstrap"
import {
  AiOutlineUpload,
  AiOutlineFileText,
  AiOutlineUser,
  AiOutlineCheckCircle,
  AiOutlineCloseCircle,
  AiOutlineWarning,
  AiOutlineEdit,
  AiOutlineDelete,
  AiOutlinePlus,
  AiOutlineEye,
  AiOutlineSafety,
  AiOutlineTeam,
} from "react-icons/ai"

import { extraerEstatutoArchivo, extraerFirmantes } from "api/estatutoApi"
import { getReglasTipoSociedad, TIPOS_SOCIEDAD } from "./Titular.constants"

// ============================================
// Componentes auxiliares
// ============================================

const ConfianzaBadge = ({ nivel }) => {
  const config = {
    ALTA: { color: "success", text: "Alta", icon: <AiOutlineCheckCircle /> },
    MEDIA: { color: "warning", text: "Media", icon: <AiOutlineWarning /> },
    BAJA: { color: "danger", text: "Baja", icon: <AiOutlineWarning /> },
    MUY_BAJA: {
      color: "dark",
      text: "Muy Baja",
      icon: <AiOutlineCloseCircle />,
    },
  }
  const cfg = config[nivel] || config.MEDIA
  return (
    <Badge
      color={cfg.color}
      className="d-flex align-items-center gap-1"
      style={{ fontSize: "0.7rem" }}
    >
      {cfg.icon} {cfg.text}
    </Badge>
  )
}

const TipoFirmaBadge = ({ tipo }) => {
  const config = {
    INDIVIDUAL: { color: "success", text: "Individual" },
    CONJUNTA: { color: "warning", text: "Conjunta" },
    INDISTINTA: { color: "info", text: "Indistinta" },
    CON_LIMITE: { color: "primary", text: "Con Límite" },
  }
  const cfg = config[tipo] || { color: "secondary", text: tipo }
  return <Badge color={cfg.color}>{cfg.text}</Badge>
}

const BiometriaBadge = ({ requiere, estado }) => {
  if (!requiere) {
    return <Badge color="secondary">No requerida</Badge>
  }

  const config = {
    PENDIENTE: {
      color: "warning",
      text: "Pendiente",
      icon: <AiOutlineWarning />,
    },
    EN_PROCESO: {
      color: "info",
      text: "En proceso",
      icon: <Spinner size="sm" />,
    },
    APROBADA: {
      color: "success",
      text: "Aprobada",
      icon: <AiOutlineCheckCircle />,
    },
    RECHAZADA: {
      color: "danger",
      text: "Rechazada",
      icon: <AiOutlineCloseCircle />,
    },
  }
  const cfg = config[estado] || config.PENDIENTE
  return (
    <Badge color={cfg.color} className="d-flex align-items-center gap-1">
      {cfg.icon} {cfg.text}
    </Badge>
  )
}

// ============================================
// Componente de carga de estatuto
// ============================================

const EstatutoUploader = ({
  onExtraccionCompleta,
  cuit,
  razonSocial,
  tipoSociedad,
}) => {
  const [archivo, setArchivo] = useState(null)
  const [cargando, setCargando] = useState(false)
  const [error, setError] = useState(null)
  const [progreso, setProgreso] = useState(0)
  const [mensajeProgreso, setMensajeProgreso] = useState("")
  const inputRef = useRef(null)

  const handleFileChange = e => {
    const file = e.target.files[0]
    if (file) {
      if (file.type !== "application/pdf") {
        setError("Solo se permiten archivos PDF")
        return
      }
      if (file.size > 20 * 1024 * 1024) {
        // 20MB
        setError("El archivo no puede superar los 20MB")
        return
      }
      setArchivo(file)
      setError(null)
    }
  }

  const handleExtraer = async () => {
    if (!archivo) return

    setCargando(true)
    setError(null)
    setProgreso(10)
    setMensajeProgreso("Subiendo archivo...")

    try {
      setProgreso(30)
      setMensajeProgreso("Analizando estatuto con IA...")

      const resultado = await extraerEstatutoArchivo(archivo, cuit, razonSocial)

      setProgreso(90)
      setMensajeProgreso("Procesando resultados...")

      if (resultado.exitoso) {
        setProgreso(100)
        setMensajeProgreso("¡Extracción completada!")

        // Esperar un momento para mostrar el 100%
        setTimeout(() => {
          onExtraccionCompleta(resultado)
        }, 500)
      } else {
        throw new Error(resultado.mensajeError || "Error en la extracción")
      }
    } catch (err) {
      console.error("Error extrayendo estatuto:", err)
      setError(err.message || "Error al procesar el estatuto")
      setProgreso(0)
    } finally {
      setCargando(false)
    }
  }

  return (
    <Card className="mb-4 border-primary">
      <CardHeader className="bg-primary text-white">
        <div className="d-flex align-items-center">
          <AiOutlineFileText size={20} className="me-2" />
          <span>Cargar Estatuto / Contrato Social</span>
        </div>
      </CardHeader>
      <CardBody>
        <p className="text-muted mb-3">
          Suba el estatuto societario o contrato social para extraer
          automáticamente los firmantes autorizados y personas relacionadas.
        </p>

        {/* Input de archivo */}
        <div
          className={`border rounded p-4 text-center mb-3 ${archivo ? "border-success bg-success bg-opacity-10" : "border-dashed"}`}
          style={{ cursor: "pointer" }}
          onClick={() => inputRef.current?.click()}
        >
          <input
            type="file"
            ref={inputRef}
            onChange={handleFileChange}
            accept="application/pdf"
            style={{ display: "none" }}
          />

          {archivo ? (
            <div>
              <AiOutlineCheckCircle size={32} className="text-success mb-2" />
              <p className="mb-1 fw-bold">{archivo.name}</p>
              <small className="text-muted">
                {(archivo.size / 1024 / 1024).toFixed(2)} MB
              </small>
            </div>
          ) : (
            <div>
              <AiOutlineUpload size={32} className="text-muted mb-2" />
              <p className="mb-1">
                Haga clic para seleccionar o arrastre el archivo
              </p>
              <small className="text-muted">PDF, máximo 20MB</small>
            </div>
          )}
        </div>

        {/* Barra de progreso */}
        {cargando && (
          <div className="mb-3">
            <div className="d-flex justify-content-between mb-1">
              <small>{mensajeProgreso}</small>
              <small>{progreso}%</small>
            </div>
            <Progress value={progreso} animated color="primary" />
          </div>
        )}

        {/* Error */}
        {error && (
          <Alert color="danger" className="mb-3">
            <AiOutlineCloseCircle className="me-2" />
            {error}
          </Alert>
        )}

        {/* Info del tipo de sociedad */}
        {tipoSociedad && (
          <Alert color="info" className="mb-3 py-2">
            <small>
              <strong>Tipo de sociedad:</strong>{" "}
              {TIPOS_SOCIEDAD.find(t => t.codigo === tipoSociedad)
                ?.descripcion || tipoSociedad}
              {" - "}
              Se buscarán:{" "}
              {getReglasTipoSociedad(tipoSociedad).personasRequeridas.join(
                ", ",
              )}
            </small>
          </Alert>
        )}

        {/* Botón extraer */}
        <Button
          color="primary"
          onClick={handleExtraer}
          disabled={!archivo || cargando}
          className="w-100"
        >
          {cargando ? (
            <>
              <Spinner size="sm" className="me-2" />
              Extrayendo información...
            </>
          ) : (
            <>
              <AiOutlineSafety className="me-2" />
              Extraer Firmantes y Personas Relacionadas
            </>
          )}
        </Button>
      </CardBody>
    </Card>
  )
}

// ============================================
// Componente de tarjeta de firmante
// ============================================

const FirmanteCard = ({
  firmante,
  index,
  onEdit,
  onDelete,
  onValidarBiometria,
  estadoBiometria,
  esRequerido,
}) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <Card className={`mb-3 ${esRequerido ? "border-primary" : ""}`}>
      <CardBody className="py-3">
        <div className="d-flex justify-content-between align-items-start">
          {/* Info principal */}
          <div className="d-flex align-items-start">
            <div
              className={`rounded-circle d-flex align-items-center justify-content-center me-3 ${
                firmante.requiereBiometria ? "bg-primary" : "bg-secondary"
              } bg-opacity-10`}
              style={{ width: 45, height: 45, minWidth: 45 }}
            >
              <AiOutlineUser
                size={24}
                className={
                  firmante.requiereBiometria ? "text-primary" : "text-secondary"
                }
              />
            </div>
            <div>
              <h6 className="mb-1">
                {firmante.nombre}
                {esRequerido && (
                  <Badge
                    color="primary"
                    className="ms-2"
                    style={{ fontSize: "0.65rem" }}
                  >
                    REQUERIDO
                  </Badge>
                )}
              </h6>
              <div className="text-muted small">
                <span className="me-3">
                  {firmante.tipoDocumento}: {firmante.numeroDocumento}
                </span>
                <Badge color="light" className="text-dark">
                  {firmante.cargo}
                </Badge>
              </div>
            </div>
          </div>

          {/* Badges de estado */}
          <div className="d-flex flex-column align-items-end gap-2">
            <TipoFirmaBadge tipo={firmante.tipoFirma} />
            {firmante.requiereBiometria && (
              <BiometriaBadge
                requiere={firmante.requiereBiometria}
                estado={estadoBiometria}
              />
            )}
            <ConfianzaBadge nivel={firmante.confianza} />
          </div>
        </div>

        {/* Detalles expandibles */}
        <Collapse isOpen={expanded}>
          <div className="mt-3 pt-3 border-top">
            <Row>
              {firmante.limiteMonto && (
                <Col md={4}>
                  <small className="text-muted d-block">Límite de monto</small>
                  <span>
                    ${firmante.limiteMonto.toLocaleString("es-AR")}{" "}
                    {firmante.monedaLimite}
                  </span>
                </Col>
              )}
              {firmante.firmaConjuntaCon &&
                firmante.firmaConjuntaCon.length > 0 && (
                  <Col md={4}>
                    <small className="text-muted d-block">
                      Firma conjunta con
                    </small>
                    <span>{firmante.firmaConjuntaCon.join(", ")}</span>
                  </Col>
                )}
              {firmante.fuente && (
                <Col md={4}>
                  <small className="text-muted d-block">Fuente</small>
                  <span>{firmante.fuente}</span>
                </Col>
              )}
            </Row>

            {firmante.operacionesAutorizadas &&
              firmante.operacionesAutorizadas.length > 0 && (
                <div className="mt-2">
                  <small className="text-muted d-block">
                    Operaciones autorizadas
                  </small>
                  <div className="d-flex flex-wrap gap-1">
                    {firmante.operacionesAutorizadas.map((op, i) => (
                      <Badge key={i} color="light" className="text-dark">
                        {op}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
          </div>
        </Collapse>

        {/* Acciones */}
        <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
          <Button
            color="link"
            size="sm"
            className="p-0 text-muted"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? "Ver menos" : "Ver más detalles"}
          </Button>

          <div className="d-flex gap-2">
            {firmante.requiereBiometria && estadoBiometria === "PENDIENTE" && (
              <Button
                color="primary"
                size="sm"
                onClick={() => onValidarBiometria(firmante)}
              >
                <AiOutlineSafety className="me-1" />
                Validar Biometría
              </Button>
            )}
            <Button
              color="light"
              size="sm"
              onClick={() => onEdit(firmante, index)}
            >
              <AiOutlineEdit />
            </Button>
            <Button color="light" size="sm" onClick={() => onDelete(index)}>
              <AiOutlineDelete className="text-danger" />
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  )
}

// ============================================
// Modal para agregar/editar firmante manual
// ============================================

const ModalFirmanteManual = ({
  isOpen,
  toggle,
  firmante,
  onSave,
  tipoSociedad,
}) => {
  const [form, setForm] = useState({
    nombre: "",
    tipoDocumento: "DNI",
    numeroDocumento: "",
    cargo: "",
    tipoFirma: "INDIVIDUAL",
    limiteMonto: "",
    requiereBiometria: true,
    fuente: "Manual",
    confianza: "ALTA",
  })

  useEffect(() => {
    if (firmante) {
      setForm({ ...firmante })
    } else {
      setForm({
        nombre: "",
        tipoDocumento: "DNI",
        numeroDocumento: "",
        cargo: "",
        tipoFirma: "INDIVIDUAL",
        limiteMonto: "",
        requiereBiometria: true,
        fuente: "Manual",
        confianza: "ALTA",
      })
    }
  }, [firmante, isOpen])

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = () => {
    if (!form.nombre || !form.numeroDocumento || !form.cargo) {
      alert("Complete los campos requeridos")
      return
    }
    onSave(form)
    toggle()
  }

  const reglas = tipoSociedad ? getReglasTipoSociedad(tipoSociedad) : null
  const cargosDisponibles = reglas
    ? [...reglas.personasRequeridas, ...reglas.personasOpcionales]
    : [
        "Presidente",
        "Director",
        "Socio Gerente",
        "Gerente",
        "Administrador",
        "Apoderado",
        "Otro",
      ]

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        {firmante ? "Editar Firmante" : "Agregar Firmante Manual"}
      </ModalHeader>
      <ModalBody>
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label>Nombre completo *</Label>
              <Input
                value={form.nombre}
                onChange={e => handleChange("nombre", e.target.value)}
                placeholder="Nombre y apellido"
              />
            </FormGroup>
          </Col>
          <Col md={3}>
            <FormGroup>
              <Label>Tipo documento</Label>
              <Input
                type="select"
                value={form.tipoDocumento}
                onChange={e => handleChange("tipoDocumento", e.target.value)}
              >
                <option value="DNI">DNI</option>
                <option value="CUIT">CUIT</option>
                <option value="CUIL">CUIL</option>
                <option value="PASAPORTE">Pasaporte</option>
              </Input>
            </FormGroup>
          </Col>
          <Col md={3}>
            <FormGroup>
              <Label>Número *</Label>
              <Input
                value={form.numeroDocumento}
                onChange={e =>
                  handleChange(
                    "numeroDocumento",
                    e.target.value.replace(/[^0-9]/g, ""),
                  )
                }
                placeholder="12345678"
              />
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label>Cargo *</Label>
              <Input
                type="select"
                value={form.cargo}
                onChange={e => handleChange("cargo", e.target.value)}
              >
                <option value="">Seleccionar...</option>
                {cargosDisponibles.map(cargo => (
                  <option key={cargo} value={cargo}>
                    {cargo}
                  </option>
                ))}
                <option value="Otro">Otro</option>
              </Input>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label>Tipo de firma</Label>
              <Input
                type="select"
                value={form.tipoFirma}
                onChange={e => handleChange("tipoFirma", e.target.value)}
              >
                <option value="INDIVIDUAL">Individual</option>
                <option value="CONJUNTA">Conjunta</option>
                <option value="INDISTINTA">Indistinta</option>
                <option value="CON_LIMITE">Con límite de monto</option>
              </Input>
            </FormGroup>
          </Col>
        </Row>

        {form.tipoFirma === "CON_LIMITE" && (
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Límite de monto ($)</Label>
                <Input
                  type="number"
                  value={form.limiteMonto}
                  onChange={e => handleChange("limiteMonto", e.target.value)}
                  placeholder="1000000"
                />
              </FormGroup>
            </Col>
          </Row>
        )}

        <FormGroup check className="mt-3">
          <Input
            type="checkbox"
            checked={form.requiereBiometria}
            // checked={true}
            onChange={e => handleChange("requiereBiometria", e.target.checked)}
            // readOnly
          />
          <Label check>Requiere validación biométrica</Label>
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Cancelar
        </Button>
        <Button color="primary" onClick={handleSubmit}>
          {firmante ? "Guardar cambios" : "Agregar firmante"}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

// ============================================
// Componente de resumen de extracción
// ============================================

const ResumenExtraccion = ({ resultado, onCerrar }) => {
  if (!resultado) return null

  return (
    <Alert
      color={resultado.confianzaGeneral === "ALTA" ? "success" : "warning"}
      className="mb-4"
    >
      <div className="d-flex justify-content-between align-items-start">
        <div>
          <h6 className="alert-heading mb-2">
            <AiOutlineCheckCircle className="me-2" />
            Extracción completada
          </h6>
          <Row>
            <Col xs="auto">
              <small className="text-muted">Tipo sociedad:</small>
              <div>
                <strong>{resultado.tipoSociedad}</strong>
              </div>
            </Col>
            <Col xs="auto">
              <small className="text-muted">Socios:</small>
              <div>
                <strong>{resultado.socios?.length || 0}</strong>
              </div>
            </Col>
            <Col xs="auto">
              <small className="text-muted">Directivos:</small>
              <div>
                <strong>{resultado.directivos?.length || 0}</strong>
              </div>
            </Col>
            <Col xs="auto">
              <small className="text-muted">Firmantes:</small>
              <div>
                <strong>{resultado.firmantes?.length || 0}</strong>
              </div>
            </Col>
            <Col xs="auto">
              <small className="text-muted">Confianza:</small>
              <div>
                <ConfianzaBadge nivel={resultado.confianzaGeneral} />
              </div>
            </Col>
          </Row>

          {resultado.observaciones && resultado.observaciones.length > 0 && (
            <div className="mt-2">
              <small className="text-muted">Observaciones:</small>
              <ul className="mb-0 ps-3">
                {resultado.observaciones.map((obs, i) => (
                  <li key={i}>
                    <small>{obs}</small>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <Button close onClick={onCerrar} />
      </div>
    </Alert>
  )
}

// ============================================
// Componente principal: Firmantes
// ============================================

const Firmantes = ({
  cuit,
  razonSocial,
  tipoSociedad,
  firmantes = [],
  setFirmantes,
  estadosBiometria = {},
  setEstadosBiometria,
  setFirmantesCompletos,
  onIniciarBiometria,
}) => {
  const [resultadoExtraccion, setResultadoExtraccion] = useState(null)
  const [modalFirmante, setModalFirmante] = useState({
    open: false,
    firmante: null,
    index: null,
  })
  const [mostrarSocios, setMostrarSocios] = useState(false)
  const [socios, setSocios] = useState([])
  const [directivos, setDirectivos] = useState([])

  // Obtener reglas según tipo de sociedad
  const reglas = tipoSociedad ? getReglasTipoSociedad(tipoSociedad) : null

  // Verificar completitud
  useEffect(() => {
    if (!reglas || !setFirmantesCompletos) return

    // Verificar que haya al menos un firmante
    if (firmantes.length === 0) {
      setFirmantesCompletos(false)
      return
    }

    // Verificar que los firmantes requeridos tengan biometría OK (si aplica)
    const firmantesConBiometriaRequerida = firmantes.filter(
      f => f.requiereBiometria,
    )
    const todosBiometriaOK = firmantesConBiometriaRequerida.every(f => {
      const estado = estadosBiometria[f.numeroDocumento]
      return estado === "APROBADA"
    })

    // Si no hay firmantes que requieran biometría, está completo
    // Si hay, deben estar todos aprobados
    const completo =
      firmantesConBiometriaRequerida.length === 0 || todosBiometriaOK
    setFirmantesCompletos(completo)
  }, [firmantes, estadosBiometria, reglas])

  // Handler cuando se completa la extracción
  const handleExtraccionCompleta = resultado => {
    setResultadoExtraccion(resultado)

    // Guardar socios y directivos para referencia
    if (resultado.socios) setSocios(resultado.socios)
    if (resultado.directivos) setDirectivos(resultado.directivos)

    // Establecer firmantes
    if (resultado.firmantes && resultado.firmantes.length > 0) {
      setFirmantes(resultado.firmantes)

      // Inicializar estados de biometría
      const nuevosEstados = {}
      resultado.firmantes.forEach(f => {
        if (f.requiereBiometria) {
          nuevosEstados[f.numeroDocumento] = "PENDIENTE"
        }
      })
      if (setEstadosBiometria) {
        setEstadosBiometria(prev => ({ ...prev, ...nuevosEstados }))
      }
    }

    // Si se detectó un tipo de sociedad diferente, alertar
    if (resultado.tipoSociedad && resultado.tipoSociedad !== tipoSociedad) {
      console.warn(
        `Tipo de sociedad detectado (${resultado.tipoSociedad}) difiere del seleccionado (${tipoSociedad})`,
      )
    }
  }

  // Handlers de firmantes
  const handleEditFirmante = (firmante, index) => {
    setModalFirmante({ open: true, firmante, index })
  }

  const handleDeleteFirmante = index => {
    if (window.confirm("¿Eliminar este firmante?")) {
      setFirmantes(prev => prev.filter((_, i) => i !== index))
    }
  }

  const handleSaveFirmante = firmante => {
    if (modalFirmante.index !== null) {
      // Editar existente
      setFirmantes(prev =>
        prev.map((f, i) => (i === modalFirmante.index ? firmante : f)),
      )
    } else {
      // Agregar nuevo
      setFirmantes(prev => [...prev, firmante])

      // Inicializar estado biometría si requiere
      if (firmante.requiereBiometria && setEstadosBiometria) {
        setEstadosBiometria(prev => ({
          ...prev,
          [firmante.numeroDocumento]: "PENDIENTE",
        }))
      }
    }
    setModalFirmante({ open: false, firmante: null, index: null })
  }

  const handleValidarBiometria = firmante => {
    if (onIniciarBiometria) {
      onIniciarBiometria(firmante)
    } else {
      // Fallback: simular inicio de proceso
      if (setEstadosBiometria) {
        setEstadosBiometria(prev => ({
          ...prev,
          [firmante.numeroDocumento]: "EN_PROCESO",
        }))
      }
      alert(`Iniciar validación biométrica para ${firmante.nombre}`)
    }
  }

  // Calcular progreso de biometría
  const firmantesConBiometria = firmantes.filter(f => f.requiereBiometria)
  const biometriasAprobadas = firmantesConBiometria.filter(
    f => estadosBiometria[f.numeroDocumento] === "APROBADA",
  ).length
  const progresoBiometria =
    firmantesConBiometria.length > 0
      ? Math.round((biometriasAprobadas / firmantesConBiometria.length) * 100)
      : 100

  return (
    <div>
      <h5 className="mb-3">
        <AiOutlineTeam className="me-2" />
        Firmantes Autorizados
      </h5>
      <p className="text-muted mb-4">
        Identifique las personas autorizadas a firmar por la sociedad. Puede
        extraerlos automáticamente del estatuto o agregarlos manualmente.
      </p>

      {/* Uploader de estatuto (solo si no hay firmantes) */}
      {firmantes.length === 0 && (
        <EstatutoUploader
          onExtraccionCompleta={handleExtraccionCompleta}
          cuit={cuit}
          razonSocial={razonSocial}
          tipoSociedad={tipoSociedad}
        />
      )}

      {/* Resumen de extracción */}
      {resultadoExtraccion && (
        <ResumenExtraccion
          resultado={resultadoExtraccion}
          onCerrar={() => setResultadoExtraccion(null)}
        />
      )}

      {/* Progreso de biometría */}
      {firmantes.length > 0 && firmantesConBiometria.length > 0 && (
        <Card className="mb-4 bg-light">
          <CardBody className="py-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span>
                <AiOutlineSafety className="me-2" />
                Validación Biométrica
              </span>
              <span className="fw-bold">
                {biometriasAprobadas}/{firmantesConBiometria.length} completadas
              </span>
            </div>
            <Progress
              value={progresoBiometria}
              color={progresoBiometria === 100 ? "success" : "primary"}
            />
          </CardBody>
        </Card>
      )}

      {/* Lista de firmantes */}
      {firmantes.length > 0 && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Firmantes ({firmantes.length})</h6>
            <div className="d-flex gap-2">
              {socios.length > 0 && (
                <Button
                  color="light"
                  size="sm"
                  onClick={() => setMostrarSocios(!mostrarSocios)}
                >
                  <AiOutlineEye className="me-1" />
                  {mostrarSocios ? "Ocultar" : "Ver"} Socios ({socios.length})
                </Button>
              )}
              <Button
                color="primary"
                size="sm"
                onClick={() =>
                  setModalFirmante({ open: true, firmante: null, index: null })
                }
              >
                <AiOutlinePlus className="me-1" />
                Agregar Manual
              </Button>
            </div>
          </div>

          {firmantes.map((firmante, index) => (
            <FirmanteCard
              key={`${firmante.numeroDocumento}-${index}`}
              firmante={firmante}
              index={index}
              onEdit={handleEditFirmante}
              onDelete={handleDeleteFirmante}
              onValidarBiometria={handleValidarBiometria}
              estadoBiometria={
                estadosBiometria[firmante.numeroDocumento] || "PENDIENTE"
              }
              esRequerido={reglas?.personasRequeridas.some(r =>
                firmante.cargo?.toLowerCase().includes(r.toLowerCase()),
              )}
            />
          ))}
        </>
      )}

      {/* Tabla de socios (colapsable) */}
      <Collapse isOpen={mostrarSocios}>
        <Card className="mt-3">
          <CardHeader className="bg-light py-2">
            <strong>Socios / Accionistas</strong>
          </CardHeader>
          <CardBody className="p-0">
            <Table size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Documento</th>
                  <th>Participación</th>
                  <th>Confianza</th>
                </tr>
              </thead>
              <tbody>
                {socios.map((socio, i) => (
                  <tr key={i}>
                    <td>{socio.nombre}</td>
                    <td>
                      {socio.tipoDocumento} {socio.numeroDocumento}
                    </td>
                    <td>{socio.porcentajeParticipacion}%</td>
                    <td>
                      <ConfianzaBadge nivel={socio.confianza} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </CardBody>
        </Card>
      </Collapse>

      {/* Alerta si no hay firmantes */}
      {firmantes.length === 0 && !resultadoExtraccion && (
        <Alert color="warning" className="mt-4">
          <AiOutlineWarning className="me-2" />
          No hay firmantes definidos. Cargue el estatuto para extraerlos
          automáticamente o agréguelos manualmente.
          <Button
            color="link"
            className="p-0 ms-2"
            onClick={() =>
              setModalFirmante({ open: true, firmante: null, index: null })
            }
          >
            Agregar manualmente
          </Button>
        </Alert>
      )}

      {/* Modal agregar/editar */}
      <ModalFirmanteManual
        isOpen={modalFirmante.open}
        toggle={() =>
          setModalFirmante({ open: false, firmante: null, index: null })
        }
        firmante={modalFirmante.firmante}
        onSave={handleSaveFirmante}
        tipoSociedad={tipoSociedad}
      />
    </div>
  )
}

export default Firmantes
